import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, message, Skeleton, Empty, Modal } from 'antd';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import { HeartOutlined, HeartFilled, PlayCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { objectToQuery } from '../../utils/Utils';
import { debounce } from 'lodash';
import { COURSE_FAVORITES_URL, COURSE_FAVORITE_URL } from '../../constants/Url';
import { setLikeCourseListAction } from '../../modules/dataWebsite/actions';

const formatDuration = (seconds) => {
  if (!seconds) return '';
  if (typeof seconds === 'string' && seconds.includes(':')) {
    return seconds;
  }
  const secs = parseInt(seconds, 10);
  if (isNaN(secs)) return '';
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const remainingSeconds = secs % 60;

  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
};

export default function LikeCoursePage({isHide}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const courses = useSelector((state) => state.dataWebsiteReducer?.likeCourseData);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');

  const loadinFirst = useRef(false);

  const fetchData = async () => {
    try {
      if (!loadinFirst?.current && courses?.length > 0) {
          setLoading(false);
      } else {
          setLoading(true);
      }
      loadinFirst.current = true;
      const query = {
        pageNo: pageNo,
        pageSize: pageSize
      };
      const res = await httpClient.get(objectToQuery(COURSE_FAVORITES_URL, query))
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        // Assume courses in favorites list are all favorited (isFavorited = true)
        const coursesWithFav = (res.data || []).map(item => ({ ...item, isFavorited: true }));
        setLikeCourseListAction(coursesWithFav);
        setTotal(res.total || res.data?.length || 0);
      } else {
        setLikeCourseListAction([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("fetchData -> error", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchData = useCallback(
    debounce(fetchData, 300),
    [pageNo, pageSize]
  );

  useEffect(() => {
    debouncedFetchData();
    return debouncedFetchData.cancel;
  }, [debouncedFetchData]);

  const handleToggleFavorite = async (course) => {
    try {
      let courseId = course.courseId || course.course || course._id || course.id;
      if (typeof courseId === 'object') {
        courseId = courseId._id || courseId.id;
      }
      
      const isFav = course.isFavorited;
      const res = isFav
        ? await httpClient.delete(COURSE_FAVORITE_URL, { data: { courseId } })
            .then(res => res.data)
            .catch(error => ({ status: 500, message: error?.message }))
        : await httpClient.post(COURSE_FAVORITE_URL, { courseId })
            .then(res => res.data)
            .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        message.success(t(res.message || "Success"));
        // Refresh the list
        fetchData();
      } else {
        message.error(res.message || t("text_operation_failed"));
      }
    } catch (error) {
      console.error(t("text_toggle_error"), error);
    }
  };

  const handlePlayVideo = (course) => {
    let videoUrl = course.videoUrl || course.videoPath || course.video || course.introVideo || "";
    if (!videoUrl && course.videoName) {
      if (course.picUrl && course.picUrl.includes('/public/')) {
        const domainAndPublic = course.picUrl.split('/public/')[0] + '/public/';
        const fileNameWithPrefix = course.picUrl.substring(course.picUrl.lastIndexOf('/') + 1);
        const originalVideoName = fileNameWithPrefix.split('_thumbnail.png')[0];
        videoUrl = domainAndPublic + originalVideoName;
      } else {
        videoUrl = course.videoName;
      }
    }
    setActiveVideoUrl(videoUrl);
    setIsVideoModalOpen(true);
  };

  return (
    <div className="esi-std-container w-full min-h-screen pb-16 px-4 md:px-0">
      
      {/* Header Tabs */}
      {
        isHide && isHide ? "" :
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-100 pb-3 mt-6 mb-8 gap-4">
        <h1 className="text-[24px] font-bold text-[#111] m-0">{t("text_favorite_course")}</h1>
      </div>
      }

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-100 p-5 flex flex-col gap-4">
              <Skeleton.Image className="!w-full !aspect-[16/9]" active />
              <Skeleton.Input active size="default" style={{ width: '80%' }} />
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </div>
          ))}
        </div>
      ) : courses.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {courses.map((rawCourse) => {
              const courseObj = (typeof rawCourse.courseId === 'object' && rawCourse.courseId !== null)
                ? rawCourse.courseId
                : (typeof rawCourse.course === 'object' && rawCourse.course !== null)
                  ? rawCourse.course
                  : rawCourse;
              const courseId = courseObj.courseId || courseObj._id || courseObj.id || (typeof rawCourse.courseId === 'string' ? rawCourse.courseId : rawCourse._id);
              const title = courseObj.title || courseObj.name || rawCourse.title || '';
              const picUrl = courseObj.picUrl || courseObj.imageUrl || courseObj.coverImage || courseObj.coverimage || rawCourse.picUrl || '';
              const intro = courseObj.intro || courseObj.courseIntro || rawCourse.intro || '';
              const price = courseObj.price ?? rawCourse.price ?? 0;
              const validityPeriod = courseObj.validityPeriod ?? rawCourse.validityPeriod ?? 0;
              const categoryName = courseObj.categoryId?.name || rawCourse.categoryId?.name || '';
              const videoDuration = courseObj.videoDuration || rawCourse.videoDuration;

              return (
                <div 
                  key={rawCourse._id || rawCourse.id || courseId}
                  className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full"
                >
                  {/* Course Thumbnail */}
                  <div 
                    className="w-full aspect-[16/9] overflow-hidden relative cursor-pointer"
                    onClick={() => {
                      if (courseId) {
                        navigate(`/online-class-member/${courseId}`);
                      }
                    }}
                  >
                    <img 
                      src={picUrl} 
                      alt={title} 
                      className="w-full h-full object-cover" 
                    />
                    <div className="absolute inset-0 bg-black/25 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                      <PlayCircleOutlined className="text-white text-4xl" />
                    </div>
                    {videoDuration && (
                      <div className="absolute bottom-2 right-2 bg-black/75 px-2 py-0.5 rounded text-[10px] text-white font-medium">
                        {formatDuration(videoDuration)}
                      </div>
                    )}
                  </div>

                  {/* Course Content details */}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex justify-between items-center mb-2.5">
                      {categoryName ? (
                        <span className="text-[14px] font-semibold gradient-text-button py-0.5 rounded uppercase">
                          {t(categoryName)}
                        </span>
                      ) : <span />}
                      <HeartFilled 
                        onClick={() => handleToggleFavorite(rawCourse)}
                        className="text-red-500 cursor-pointer transition-colors" 
                      />
                    </div>

                    {/* Course Title */}
                    <h3 
                      onClick={() => {
                        if (courseId) {
                          navigate(`/online-class-member/${courseId}`);
                        }
                      }}
                      className="text-[14px] sm:text-base font-bold text-gray-900 leading-snug transition-colors mb-4 line-clamp-1 cursor-pointer hover:text-[#e91e63]"
                    >
                      {t(title) || ""}
                    </h3>

                    <p className="text-gray-500 text-xs sm:text-[13px] leading-relaxed line-clamp-3 mb-6 flex-1">
                      {t(intro) || ""}
                    </p>

                    <div className="flex justify-between items-center pt-3 border-t border-gray-50 mt-auto">
                      <span className="text-base sm:text-lg font-black text-gray-900">
                        &yen; {price}
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {t("text_member_can_buy")} {validityPeriod} {t('text_day_no')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('text_no_fav_course')} />
      )}

      {(loading || total > 0 || courses.length > 0) && (
        <div className="flex justify-end mt-12">
          <CustomPagination
            current={pageNo}
            pageSize={pageSize}
            total={loading ? 8 : (total || courses.length || 0)}
            onChange={(page, limit) => {
              if (limit !== pageSize) {
                setPageSize(limit);
                setPageNo(1);
              } else {
                setPageNo(page);
              }
            }}
          />
        </div>
      )}

      {/* Video Modal */}
      <Modal
        open={isVideoModalOpen}
        onCancel={() => setIsVideoModalOpen(false)}
        footer={null}
        destroyOnClose
        width={800}
        styles={{ body: { padding: 0, backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
        centered
      >
        <video 
          className="w-full aspect-video rounded-lg" 
          controls 
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          autoPlay 
          src={activeVideoUrl}
        >
          {t("text_tag_not_support")}
        </video>
      </Modal>

    </div>
  );
}
